import { Rocket } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { AccessGroup } from '@/api/auth';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import { INTERVIEW_STEP, SETUP_STEPS, type SetupState, type SetupStep } from './setupSteps';

/**
 * `DESIGN.md` §6.2's first-run path: **"A brand-new tenant must not see a grid of zeros. Until
 * there is at least one submitted interview, the dashboard is replaced by a short setup path."**
 *
 * Replaced, not preceded — the caller returns this *instead of* the dashboard, on
 * `has_submitted_interview` alone. The three step booleans decide which steps are struck through
 * and where the action points; they are not the trigger, and using them as one is how a tenant with
 * a vacancy, its questions and a candidate but nothing submitted got the grid of zeros §6.2 opens
 * by forbidding.
 *
 * "Steps already done render struck through in `ink-500`" — so the path shows the whole of itself
 * and where in it this tenant stands, rather than naming one step and hiding the rest.
 */
export function SetupPath({ setup, groups }: { setup: SetupState; groups: AccessGroup[] }) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();

  const mayPerform = (step: SetupStep) => step.performedBy.some((group) => groups.includes(group));
  /**
   * The step in hand for **this** viewer: the first one that is not done and that they may perform.
   * A Recruiter cannot create a vacancy or a question bank (§6.8, §6.9), so their action is the
   * candidate — not a button that lands them on a screen §5 refuses them.
   *
   * ⚠️ **It deliberately skips an unmet step the viewer may not perform, rather than waiting for
   * it.** On a brand-new tenant a Recruiter is offered *"Add a candidate"* while steps 1 and 2 are
   * still undone, and a `Consideration` needs a `Role` (`backend/CONTEXT.md`) — so that button leads to
   * §6.3, and §6.4's dialog behind it has a vacancy field with nothing in it. The alternative is to
   * offer a Recruiter no action at all and a sentence saying an admin has to go first, and **§6.2
   * defines no such actionless fourth variant of the setup path** — §8 gives an empty state exactly
   * *"icon, one sentence, one action"* — so inventing one here would be design done in a fix pass.
   * **That trade is unchanged**, and it is the only part of this note that has never moved.
   *
   * ⚠️ **This note has now gone stale twice; read both corrections before trusting a word of it.**
   *
   *  * **TASK-077 (2026-07-29)** retracted *"skipping is also harmless today — `/candidates` answers the
   *    placeholder for everyone"*, which TASK-062 had made false.
   *  * **BUG-020 (2026-07-29)** overtook what replaced it. This note went on to say that the sentence a
   *    Recruiter needs *"is not designed"* and that §6.4's dialog shows *"a **disabled** vacancy `Select`
   *    … and no sentence saying why"*. It **is** designed and it **is** said: `EmptyCatalogueNotice` in
   *    `pages/Candidates/AddCandidateDialog.tsx` renders *"No vacancies yet."* and one sentence beside
   *    the (still disabled) field once `GET /roles/` answers **empty**, worded per group — an Admin gets
   *    a link to §6.8, a Recruiter is told to ask an admin and is offered nothing, because
   *    `POST /roles/` refuses them. `DESIGN.md` §6.4 now carries the three states of that field as a
   *    table. The *"design done in a fix pass"* argument above survives only in its narrow form — about
   *    a fourth variant of **§6.2's own path** — and never about that sentence, which was designed, and
   *    designed where the field is.
   *
   * So the step this file offers a Recruiter now leads to a form that explains itself, and it was fixed
   * **there** rather than here, exactly as the retracted clause said it would have to be. **The trigger
   * to revisit remains §6.4's own screen, not this file**: this file's behaviour is asserted as it stands
   * in `pages/screens.test.tsx` ("offers a Recruiter the step they may perform"), so changing it is a
   * visible decision rather than a silent one.
   *
   * ⚠️ **What this card does not do**, measured in a live browser during BUG-020's pass: it does not
   * reach §6.4's dialog itself. Its one interactive element is this button — *Add a vacancy* for an
   * Admin, *"Add a candidate"* for a Recruiter — and the numbered steps beside it are label text, not
   * links, so the notice above is read one screen later, when the dialog is opened from §6.3.
   */
  const next = SETUP_STEPS.find((step) => !step.done(setup) && mayPerform(step)) ?? INTERVIEW_STEP;

  return (
    <section className="mt-10" aria-label={t('setup.title')}>
      <Eyebrow>{t('setup.title')}</Eyebrow>

      <EmptyState
        icon={Rocket}
        message={t('setup.nothingYet')}
        action={
          <Button variant="primary" onClick={() => void navigate(next.to)}>
            {t(`setup.${next.key}`)}
          </Button>
        }
      >
        <ol className="flex flex-wrap items-baseline justify-center gap-x-6 gap-y-1">
          {SETUP_STEPS.map((step, index) => (
            <li
              key={step.key}
              className={cn(
                'flex items-baseline gap-2 text-sm',
                step.done(setup) ? 'text-ink-500 line-through' : 'text-ink-700',
              )}
            >
              <span className={cn(type.numeral, 'text-xs text-ink-500')}>{index + 1}</span>
              {t(`setup.${step.key}`)}
            </li>
          ))}
        </ol>
      </EmptyState>
    </section>
  );
}
